import { Navigate } from "react-router-dom";
import { useState } from "react";
import { useAdminStats, useAdminMe } from "../hooks/useApi";
import { formatNumber, formatTomanInt } from "../utils/format";
import "../admin-theme.css";

// ── Metric Card ──
function MetricCard({
  icon,
  label,
  value,
  dark,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  dark?: boolean;
}) {
  return (
    <div className="admin-metric-card">
      <div className={`admin-metric-card-accent ${dark ? "admin-metric-card-accent-dark" : ""}`} />
      <div className="admin-metric-card-content">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium" style={{ color: "var(--admin-text-tertiary)" }}>{label}</span>
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ background: dark ? "rgba(5, 96, 93, 0.1)" : "rgba(7, 135, 131, 0.08)" }}
          >
            {icon}
          </div>
        </div>
        <div className="flex items-end justify-between">
          <span className="text-2xl font-bold" style={{ color: "var(--admin-text-primary)" }}>
            {value}
          </span>
        </div>
      </div>
    </div>
  );
}

// ── Radial Bar helpers (public plans) ──
const RADIAL_SIZE = 240;
const RADIAL_CX = RADIAL_SIZE / 2;
const RADIAL_CY = RADIAL_SIZE / 2 + 6;
const RADIAL_BAR_WIDTH = 14;
const RADIAL_START_ANGLE = -90;
const RADIAL_MAX_SWEEP = 270;
const RADIAL_PALETTE = ["#078783", "#59AFAC", "#05605D", "#34D399", "#60A5FA", "#A78BFA", "#FB923C", "#F472B6"];
const OTHERS_COLOR = "#94A3B8";

function polar(cx: number, cy: number, r: number, angleDeg: number) {
  const rad = (angleDeg * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}
function arcPath(cx: number, cy: number, r: number, a0: number, a1: number): string {
  const start = polar(cx, cy, r, a0);
  const end = polar(cx, cy, r, a1);
  const largeArc = Math.abs(a1 - a0) > 180 ? 1 : 0;
  return `M ${start.x.toFixed(2)} ${start.y.toFixed(2)} A ${r} ${r} 0 ${largeArc} 1 ${end.x.toFixed(2)} ${end.y.toFixed(2)}`;
}

function PublicPlanRadialChart({ data }: { data: { plan_id: number; plan_name: string; user_count: number }[] }) {
  const [hovered, setHovered] = useState<string | null>(null);

  if (!data || data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16" style={{ color: "var(--admin-text-tertiary)" }}>
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" className="mb-3 opacity-40" stroke="currentColor" strokeWidth="1.5">
          <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
          <circle cx="9" cy="7" r="4" />
        </svg>
        <p className="text-sm">هیچ کاربری یافت نشد</p>
        <p className="text-xs mt-1" style={{ color: "var(--admin-text-tertiary)" }}>داده‌ای برای نمایش وجود ندارد</p>
      </div>
    );
  }

  const sorted = [...data].sort((a, b) => b.user_count - a.user_count);
  const maxValue = Math.max(1, ...sorted.map((d) => d.user_count));
  const total = sorted.reduce((s, d) => s + d.user_count, 0);
  const radiusFor = (i: number) => 34 + i * (RADIAL_BAR_WIDTH + 3);
  const colorFor = (name: string, idx: number) => (name === "سایر" ? OTHERS_COLOR : RADIAL_PALETTE[idx % RADIAL_PALETTE.length]);

  return (
    <div className="flex flex-col items-center" dir="ltr">
      <div className="relative w-full max-w-[280px]">
        <svg viewBox={`0 0 ${RADIAL_SIZE} ${RADIAL_SIZE}`} className="w-full block">
          {sorted.map((it, i) => {
            const r = radiusFor(i);
            const frac = it.user_count / maxValue;
            const color = colorFor(it.plan_name, i);
            const dimmed = hovered !== null && hovered !== it.plan_name;
            const isHovered = hovered === it.plan_name;
            const end = RADIAL_START_ANGLE + RADIAL_MAX_SWEEP * frac;
            return (
              <g
                key={it.plan_id}
                onMouseEnter={() => setHovered(it.plan_name)}
                onMouseLeave={() => setHovered(null)}
                style={{ cursor: "pointer", opacity: dimmed ? 0.35 : 1, transition: "opacity 200ms" }}
              >
                <path
                  d={arcPath(RADIAL_CX, RADIAL_CY, r, RADIAL_START_ANGLE, RADIAL_START_ANGLE + RADIAL_MAX_SWEEP)}
                  stroke="#E5E7EB"
                  strokeWidth={RADIAL_BAR_WIDTH}
                  strokeLinecap="round"
                  fill="none"
                />
                {frac > 0.001 && (
                  <path
                    d={arcPath(RADIAL_CX, RADIAL_CY, r, RADIAL_START_ANGLE, end)}
                    stroke={color}
                    strokeWidth={RADIAL_BAR_WIDTH}
                    strokeLinecap="round"
                    fill="none"
                    style={{
                      filter: isHovered ? `drop-shadow(0 2px 6px ${color}66)` : undefined,
                    }}
                  />
                )}
              </g>
            );
          })}
          <text
            x={RADIAL_CX}
            y={RADIAL_CY - 4}
            textAnchor="middle"
            className="font-number"
            style={{
              fontSize: 26,
              fontWeight: 700,
              fill: "var(--admin-text-primary)",
              paintOrder: "stroke",
              stroke: "#fff",
              strokeWidth: 6,
              strokeLinejoin: "round",
            }}
          >
            {formatNumber(total)}
          </text>
          <text
            x={RADIAL_CX}
            y={RADIAL_CY + 16}
            textAnchor="middle"
            style={{
              fontSize: 11,
              fill: "var(--admin-text-tertiary)",
              paintOrder: "stroke",
              stroke: "#fff",
              strokeWidth: 4,
              strokeLinejoin: "round",
            }}
          >
            کاربر فعال
          </text>
        </svg>
        {hovered && (() => {
          const it = sorted.find((x) => x.plan_name === hovered);
          if (!it) return null;
          const pct = total > 0 ? (it.user_count / total) * 100 : 0;
          const idx = sorted.findIndex((x) => x.plan_name === hovered);
          const color = colorFor(it.plan_name, idx);
          return (
            <div
              dir="rtl"
              className="absolute z-30 left-1/2 -translate-x-1/2 top-0 px-3 py-2 rounded-xl whitespace-nowrap pointer-events-none"
              style={{ background: "rgba(26,26,26,0.92)", color: "#fff", boxShadow: "0 8px 24px rgba(0,0,0,0.18)" }}
            >
              <div className="flex items-center justify-between gap-4">
                <span className="flex items-center gap-2 text-xs">
                  <span className="w-2 h-2 rounded-full" style={{ background: color }} />
                  {it.plan_name}
                </span>
                <span className="text-xs font-bold" style={{ color: "#34D399" }}>{formatNumber(it.user_count)} نفر</span>
              </div>
              <div className="mt-1 flex items-center justify-between gap-4">
                <span className="text-[11px]" style={{ opacity: 0.7 }}>سهم</span>
                <span className="text-xs">{pct.toLocaleString("fa-IR", { maximumFractionDigits: 1 })}٪</span>
              </div>
            </div>
          );
        })()}
      </div>

      <div className="w-full mt-4 space-y-1.5">
        {sorted.map((it, idx) => {
          const color = colorFor(it.plan_name, idx);
          const pct = total > 0 ? (it.user_count / total) * 100 : 0;
          return (
            <div
              key={it.plan_id}
              className={`flex items-center justify-between gap-3 px-2 py-2 rounded-xl transition-colors ${hovered === it.plan_name ? "bg-gray-50" : ""}`}
              onMouseEnter={() => setHovered(it.plan_name)}
              onMouseLeave={() => setHovered(null)}
              style={{ cursor: "pointer" }}
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: color }} />
                <span className="text-sm text-text-primary truncate" style={{ color: "var(--admin-text-primary)" }}>{it.plan_name}</span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-sm font-bold font-number" style={{ color: "var(--admin-text-primary)" }}>{formatNumber(it.user_count)}</span>
                <span className="text-[11px] w-12 text-left" style={{ color: "var(--admin-text-tertiary)" }}>{pct.toLocaleString("fa-IR", { maximumFractionDigits: 1 })}٪</span>
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-[11px] mt-3 text-center" style={{ color: "var(--admin-text-tertiary)" }}>
        پلن رایگان لحاظ شده؛ پلن‌های خصوصی به صورت «سایر» تجمیع شده‌اند
      </p>
    </div>
  );
}

// ── Pie Chart (financial) ──
const PIE_COLORS = {
  cost: "#DC2626",
  income: "#078783",
  savings: "#D97706",
  wallet: "#6366F1",
};

function FinancePieChart({ cost, income, savings, wallet }: { cost: number; income: number; savings: number; wallet: number }) {
  const [hovered, setHovered] = useState<string | null>(null);
  const slices = [
    { key: "income", label: "درآمد کل", value: income, color: PIE_COLORS.income },
    { key: "cost", label: "هزینه کل", value: cost, color: PIE_COLORS.cost },
    { key: "savings", label: "صرفه‌جویی کل", value: savings, color: PIE_COLORS.savings },
    { key: "wallet", label: "موجودی کیف پول کاربران", value: wallet, color: PIE_COLORS.wallet },
  ];
  const total = slices.reduce((s, x) => s + x.value, 0);

  if (total <= 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16" style={{ color: "var(--admin-text-tertiary)" }}>
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" className="mb-3 opacity-40" stroke="currentColor" strokeWidth="1.5">
          <circle cx="12" cy="12" r="10" />
          <path d="M12 6v6l4 2" />
        </svg>
        <p className="text-sm">داده‌ای برای نمایش وجود ندارد</p>
        <p className="text-xs mt-1">تمام مقادیر صفر است</p>
      </div>
    );
  }

  const size = 220;
  const cx = size / 2;
  const cy = size / 2;
  const r = 78;
  const innerR = 46;

  let currentAngle = -90;
  const segments = slices.map((s) => {
    const sweep = total > 0 ? (s.value / total) * 360 : 0;
    const start = currentAngle;
    const end = currentAngle + sweep;
    currentAngle = end;
    return { ...s, start, end, sweep };
  });

  function piePath(startAngle: number, endAngle: number, outerR: number, innerR: number) {
    const startOuter = polar(cx, cy, outerR, startAngle);
    const endOuter = polar(cx, cy, outerR, endAngle);
    const startInner = polar(cx, cy, innerR, endAngle);
    const endInner = polar(cx, cy, innerR, startAngle);
    const large = endAngle - startAngle > 180 ? 1 : 0;
    return `M ${startOuter.x.toFixed(2)} ${startOuter.y.toFixed(2)} A ${outerR} ${outerR} 0 ${large} 1 ${endOuter.x.toFixed(2)} ${endOuter.y.toFixed(2)} L ${startInner.x.toFixed(2)} ${startInner.y.toFixed(2)} A ${innerR} ${innerR} 0 ${large} 0 ${endInner.x.toFixed(2)} ${endInner.y.toFixed(2)} Z`;
  }

  return (
    <div className="flex flex-col items-center" dir="ltr">
      <div className="relative">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="block">
          {segments.map((seg) => {
            if (seg.value <= 0) return null;
            const isHovered = hovered === seg.key;
            const outer = isHovered ? r + 6 : r;
            return (
              <path
                key={seg.key}
                d={piePath(seg.start, seg.end, outer, innerR)}
                fill={seg.color}
                stroke="#fff"
                strokeWidth={2}
                style={{ cursor: "pointer", transition: "all 200ms ease", opacity: hovered && !isHovered ? 0.5 : 1 }}
                onMouseEnter={() => setHovered(seg.key)}
                onMouseLeave={() => setHovered(null)}
              />
            );
          })}
          <circle cx={cx} cy={cy} r={innerR - 1} fill="#fff" />
          <text x={cx} y={cy - 2} textAnchor="middle" style={{ fontSize: 18, fontWeight: 800, fill: "var(--admin-text-primary)" }} className="font-number">
            {formatNumber(Math.round(total))}
          </text>
          <text x={cx} y={cy + 14} textAnchor="middle" style={{ fontSize: 11, fill: "var(--admin-text-tertiary)" }}>
            تومان
          </text>
        </svg>
        {hovered && (() => {
          const seg = segments.find((s) => s.key === hovered);
          if (!seg) return null;
          const pct = total > 0 ? (seg.value / total) * 100 : 0;
          return (
            <div
              dir="rtl"
              className="absolute left-1/2 -translate-x-1/2 -top-1 px-3 py-2 rounded-xl whitespace-nowrap pointer-events-none"
              style={{ background: "rgba(26,26,26,0.92)", color: "#fff", boxShadow: "0 8px 24px rgba(0,0,0,0.18)" }}
            >
              <div className="flex items-center gap-2 text-xs">
                <span className="w-2 h-2 rounded-full" style={{ background: seg.color }} />
                {seg.label}
              </div>
              <div className="text-xs font-bold mt-1">{formatNumber(Math.round(seg.value))} تومان</div>
              <div className="text-[11px] mt-0.5" style={{ opacity: 0.8 }}>{pct.toLocaleString("fa-IR", { maximumFractionDigits: 1 })}٪ از کل</div>
            </div>
          );
        })()}
      </div>

      <div className="w-full mt-5 space-y-2">
        {segments.map((seg) => {
          const pct = total > 0 ? (seg.value / total) * 100 : 0;
          return (
            <div
              key={seg.key}
              className={`flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl border transition-all ${hovered === seg.key ? "shadow-sm" : ""}`}
              style={{
                background: hovered === seg.key ? "#F9FAFB" : "#fff",
                borderColor: hovered === seg.key ? seg.color + "40" : "var(--admin-border)",
                cursor: "pointer",
              }}
              onMouseEnter={() => setHovered(seg.key)}
              onMouseLeave={() => setHovered(null)}
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <span className="w-3 h-3 rounded-full shrink-0" style={{ background: seg.color }} />
                <span className="text-sm font-medium truncate" style={{ color: "var(--admin-text-primary)" }}>{seg.label}</span>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-sm font-bold font-number" style={{ color: "var(--admin-text-primary)" }}>{formatNumber(Math.round(seg.value))}</span>
                <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: seg.color + "14", color: seg.color }}>{pct.toLocaleString("fa-IR", { maximumFractionDigits: 1 })}٪</span>
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-[11px] mt-3 text-center" style={{ color: "var(--admin-text-tertiary)" }}>
        تمام مقادیر به تومان نمایش داده می‌شود
      </p>
    </div>
  );
}

export default function AdminDashboardPage() {
  const { data: admin, isLoading: adminLoading } = useAdminMe();
  const { data: stats, isLoading } = useAdminStats();

  const canViewStats = admin?.is_admin || (admin?.permissions || []).includes("stats.view");

  if (adminLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <svg className="animate-spin h-8 w-8" viewBox="0 0 24 24" fill="none" style={{ color: "var(--admin-primary)" }}>
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
    );
  }

  if (!canViewStats) {
    return <Navigate to="/admin/tickets" replace />;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <svg className="animate-spin h-8 w-8" viewBox="0 0 24 24" fill="none" style={{ color: "var(--admin-primary)" }}>
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
    );
  }

  const metrics = [
    {
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#078783" strokeWidth="1.5" strokeLinecap="round">
          <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
          <circle cx="9" cy="7" r="4" />
        </svg>
      ),
      label: "کل کاربران",
      value: formatNumber(stats?.total_users),
    },
    {
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#078783" strokeWidth="1.5" strokeLinecap="round">
          <line x1="12" y1="1" x2="12" y2="23" />
          <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
        </svg>
      ),
      label: "درآمد کل",
      value: formatTomanInt(stats?.total_revenue),
      dark: true,
    },
    {
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#078783" strokeWidth="1.5" strokeLinecap="round">
          <path d="M12 2a10 10 0 1010 10" />
          <path d="M12 12l3-3" />
          <circle cx="12" cy="12" r="2" />
        </svg>
      ),
      label: "مدل‌های فعال",
      value: formatNumber(stats?.active_models_count),
    },
    {
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#078783" strokeWidth="1.5" strokeLinecap="round">
          <line x1="12" y1="1" x2="12" y2="23" />
          <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
        </svg>
      ),
      label: "هزینه کل",
      value: formatNumber(stats?.total_expenses),
    },
  ];

  const publicPlanData: { plan_id: number; plan_name: string; user_count: number }[] =
    (stats as unknown as { public_plan_subscribers?: { plan_id: number; plan_name: string; user_count: number }[] })?.public_plan_subscribers || [];

  const costToman = Number(stats?.total_expenses ?? 0);
  const incomeToman = Number(stats?.total_revenue ?? 0) / 10;
  const savingsToman = Number(stats?.total_customer_caching_discount_irr ?? 0) / 10;
  const walletToman = Number((stats as unknown as { total_wallet_balance?: number | string })?.total_wallet_balance ?? 0) / 10;
  const codecraftKeyCapacity = (stats as unknown as {
    codecraft_key_capacity?: {
      key_number: number;
      reserved_rpm: number;
      rpm_limit: number;
      rpm_percent: number;
      reserved_tpm: number;
      tpm_limit: number;
      tpm_percent: number;
    }[];
  })?.codecraft_key_capacity ?? [];
  const codecraftOverbookFactor = Number(
    (stats as unknown as { codecraft_aggregate_overbook_factor?: number })
      ?.codecraft_aggregate_overbook_factor ?? 2,
  );
  const fixedPlanSubscribers = (stats as unknown as {
    fixed_plan_subscribers?: { plan_id: number; plan_name: string; user_count: number }[];
  })?.fixed_plan_subscribers ?? [];
  const maxFixedPlanUsers = Math.max(1, ...fixedPlanSubscribers.map((plan) => plan.user_count));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold" style={{ color: "var(--admin-text-primary)" }}>داشبورد</h1>
        <p className="text-sm mt-1" style={{ color: "var(--admin-text-secondary)" }}>خلاصه وضعیت سیستم و آمار کلی</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {metrics.map((metric, i) => (
          <MetricCard key={i} {...metric} />
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="admin-card p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold" style={{ color: "var(--admin-text-primary)" }}>کاربران بر اساس پلن</h2>
            <span className="admin-badge admin-badge-info">شامل رایگان و «سایر»</span>
          </div>
          <PublicPlanRadialChart data={publicPlanData} />
        </div>

        <div className="admin-card p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold" style={{ color: "var(--admin-text-primary)" }}>نمای مالی</h2>
            <span className="admin-badge" style={{ background: "var(--admin-bg-hover)", color: "var(--admin-text-secondary)" }}>تومان</span>
          </div>
          <FinancePieChart cost={costToman} income={incomeToman} savings={savingsToman} wallet={walletToman} />
        </div>
      </div>

      {(codecraftKeyCapacity.length > 0 || fixedPlanSubscribers.length > 0) && (
        <section className="grid grid-cols-1 2xl:grid-cols-2 gap-5" aria-label="آمار تعرفه ثابت CodeCraft">
          {codecraftKeyCapacity.length > 0 && (
            <div className="admin-card p-5" aria-labelledby="codecraft-capacity-title">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <div>
              <h2 id="codecraft-capacity-title" className="text-base font-semibold" style={{ color: "var(--admin-text-primary)" }}>
                ظرفیت رزروشده کلیدهای CodeCraft
              </h2>
              <p className="text-xs mt-1" style={{ color: "var(--admin-text-tertiary)" }}>
                درصد بر اساس سقف هر کلید پس از اعمال ضریب رزرو محاسبه می‌شود
              </p>
            </div>
            <span className="admin-badge admin-badge-info">
              ضریب رزرو: {codecraftOverbookFactor.toLocaleString("fa-IR", { maximumFractionDigits: 2 })}×
            </span>
          </div>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {codecraftKeyCapacity.map((key) => (
              <div key={key.key_number} className="rounded-xl p-4" style={{ background: "var(--admin-bg-hover)" }}>
                <h3 className="text-sm font-bold mb-4" style={{ color: "var(--admin-text-primary)" }}>
                  کلید {formatNumber(key.key_number)}
                </h3>
                {([
                  { label: "RPM", reserved: key.reserved_rpm, limit: key.rpm_limit, percent: key.rpm_percent },
                  { label: "TPM", reserved: key.reserved_tpm, limit: key.tpm_limit, percent: key.tpm_percent },
                ]).map((metric) => (
                  <div key={metric.label} className="mb-3 last:mb-0">
                    <div className="flex items-center justify-between gap-3 text-xs mb-2">
                      <span className="font-semibold" style={{ color: "var(--admin-text-secondary)" }}>{metric.label}</span>
                      <span className="font-number" style={{ color: "var(--admin-text-primary)" }}>
                        {formatNumber(metric.reserved)} / {formatNumber(metric.limit)} ({metric.percent.toLocaleString("fa-IR", { maximumFractionDigits: 1 })}٪)
                      </span>
                    </div>
                    <div className="h-2 rounded-full overflow-hidden" style={{ background: "var(--admin-bg-card)" }}>
                      <div
                        className="h-full rounded-full transition-[width]"
                        style={{
                          width: `${Math.min(100, Math.max(0, metric.percent))}%`,
                          background: metric.percent >= 100 ? "var(--admin-error)" : "var(--admin-primary)",
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
            </div>
          )}

          {fixedPlanSubscribers.length > 0 && (
            <div className="admin-card p-5" aria-labelledby="fixed-plan-subscribers-title">
              <div className="flex items-center justify-between gap-3 mb-5">
                <div>
                  <h2 id="fixed-plan-subscribers-title" className="text-base font-semibold" style={{ color: "var(--admin-text-primary)" }}>
                    کاربران تعرفه‌های ثابت
                  </h2>
                  <p className="text-xs mt-1" style={{ color: "var(--admin-text-tertiary)" }}>
                    تعداد اشتراک‌های فعال به تفکیک پلن CodeCraft
                  </p>
                </div>
                <span className="admin-badge admin-badge-info">
                  مجموع: {formatNumber(fixedPlanSubscribers.reduce((total, plan) => total + plan.user_count, 0))}
                </span>
              </div>
              <div className="space-y-4" role="img" aria-label="نمودار میله‌ای تعداد کاربران هر تعرفه ثابت">
                {fixedPlanSubscribers.map((plan) => (
                  <div key={plan.plan_id}>
                    <div className="flex items-center justify-between gap-3 text-xs mb-2">
                      <span className="font-medium truncate" style={{ color: "var(--admin-text-secondary)" }} title={plan.plan_name}>
                        {plan.plan_name}
                      </span>
                      <span className="font-bold font-number shrink-0" style={{ color: "var(--admin-text-primary)" }}>
                        {formatNumber(plan.user_count)}
                      </span>
                    </div>
                    <div className="h-3 rounded-full overflow-hidden" style={{ background: "var(--admin-bg-hover)" }}>
                      <div
                        className="h-full rounded-full transition-[width]"
                        style={{
                          width: `${plan.user_count / maxFixedPlanUsers * 100}%`,
                          background: "var(--admin-primary)",
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
